import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { DIRECT_DEBIT_VERIFICATION_MAX_ATTEMPTS } from 'config/app-config';
import { usePartner } from 'components/app/routes/usePartner';

import {
  selectDetails,
  selectAttempts,
  incrementAttempts,
  setDirectDebitField,
  setDirectDebitError
} from 'state/direct-debit/direct-debit-slice';
import {
  ResidentialSupplyAddress422Error,
  ResidentialNonSupplyAddress422Error,
  ComercialBankAccount422Error,
  BankAccountMaxAttempts422Error,
  ComercialBankAccount412Error,
  BankAccountValidationAPIError
} from 'errors/BankAccountValidationError';
import { pushEvent } from 'state/analytics/analytics-slice';

const getBankValidationErrorType = ({
  isCommercial,
  hasReachedMaxAttempts,
  isSupplyAddress,
  errorStatus
}) => {
  if (hasReachedMaxAttempts) {
    return BankAccountMaxAttempts422Error;
  }

  if (['500', '400'].includes(errorStatus)) {
    return BankAccountValidationAPIError;
  }

  if (errorStatus === '412') {
    return ComercialBankAccount412Error;
  }

  if (isCommercial) {
    return ComercialBankAccount422Error;
  }

  // residential bank account errors
  if (isSupplyAddress) {
    return ResidentialSupplyAddress422Error;
  } else {
    return ResidentialNonSupplyAddress422Error;
  }
};

export const useDirectDebitVerificationError = () => {
  const dispatch = useDispatch();

  const { isCommercial, isSupplyAddress } = useSelector(
    selectDetails,
    shallowEqual
  );
  const { ddMaxAttemps } = usePartner();
  const attemptsCount = useSelector(selectAttempts, shallowEqual);
  let hasReachedMaxAttempts = false;

  return (errors) => {
    const bankValidationError = errors?.[0];
    const errorStatus = bankValidationError?.status;
    if (errorStatus === '422') {
      dispatch(incrementAttempts(attemptsCount + 1));
      hasReachedMaxAttempts = attemptsCount >= ddMaxAttemps - 1;
    }

    if (errorStatus === '412') {
      dispatch(setDirectDebitField({ name: 'isCommercial', value: true }));
    }

    if (['500', '400', '412', '422'].includes(errorStatus)) {
      const ErrorType = getBankValidationErrorType({
        isCommercial,
        hasReachedMaxAttempts,
        isSupplyAddress,
        errorStatus
      });
      const error = new ErrorType(bankValidationError);
      const bankValidationErrors = [error.toJson()];
      dispatch(setDirectDebitError(bankValidationErrors));
      const analyticsEvent = {
        category: 'error',
        status: errorStatus,
        message: bankValidationError.errorCode,
        ...(errorStatus === '422' && {
          currentAttempt: attemptsCount + 1,
          maxAttempt: DIRECT_DEBIT_VERIFICATION_MAX_ATTEMPTS
        })
      };
      dispatch(pushEvent(analyticsEvent));
      return bankValidationErrors;
    }
    return errors;
  };
};
